import { Controller } from "@hotwired/stimulus";
import * as THREE from "three";

// Connects to data-controller="shipment-item-previewer"
export default class extends Controller {
  static values = {
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
    length: { type: Number, default: 0 },
    selectedVariant: { type: Number, default: 0 },
    lengthText: String,
    widthText: String,
    heightText: String,
    lengthAsWidthText: String,
    lengthAsHeightText: String,
    defaultText: String,
    elementId: Number,
  };

  static targets = ["preview", "canvasWrapper"];

  initialize() {
    this.resizeScheduled = false;
  }

  connect() {
    this.element.addEventListener("shown.bs.modal", () => {
      this.createElements();
      this.generateVariants();
      this.updateLegend();
      this.initScene();
      this.addBox();
      this.renderOnce();

      window.addEventListener("resize", (this.resizeBound = this.resize.bind(this)));
    });
  }

  createElements() {
    // Create canvas
    this.canvas = document.createElement("canvas");
    Object.assign(this.canvas.style, {
      width: "100%",
      height: "100%",
      display: "block",
      backgroundColor: "#f0f0f0",
      border: "1px solid #ccc",
    });
    this.canvasWrapperTarget.appendChild(this.canvas);

    // Create legend box
    this.legend = document.createElement("div");
    Object.assign(this.legend.style, {
      position: "absolute",
      bottom: "10px",
      left: "10px",
      backgroundColor: "rgba(255,255,255,0.9)",
      padding: "6px 10px",
      borderRadius: "4px",
      fontSize: "13px",
      fontFamily: "monospace",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      zIndex: 20,
    });
    this.canvasWrapperTarget.appendChild(this.legend);
  }

  generateVariants() {
    this.variants = [];
    const { widthValue: w, heightValue: h, lengthValue: l } = this;

    // Generate all possible orientations
    this.variants.push({ width: w, height: h, length: l, name: this.defaultTextValue });

    const highestDimension = Math.max(w, h, l);

    if (highestDimension == w) {
      this.variants.push({ width: l, height: h, length: w, name: this.lengthAsWidthTextValue });
    }

    if (highestDimension == h) {
      this.variants.push({ width: w, height: l, length: h, name: this.lengthAsHeightTextValue });
    }

    // Create radio buttons for each variant
    this.variants.forEach((variant, index) => {
      const container = document.createElement("label");
      container.classList.add("variant-container");
      if (index === 0) {
        container.classList.add("selected");
      }

      // Create mini preview canvas
      const miniCanvas = document.createElement("canvas");
      miniCanvas.width = 60;
      miniCanvas.height = 60;

      // Radio input
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "box-variant";
      radio.value = index;
      radio.checked = index === 0;
      radio.dataset.action = "click->shipment-item-previewer#selectVariant";

      // Label
      const label = document.createElement("div");
      label.textContent = `${variant.name} (${variant.length}x${variant.width}x${variant.height} cm)`;
      label.style.fontSize = "12px";
      label.style.textAlign = "center";

      container.appendChild(miniCanvas);
      container.appendChild(label);
      container.appendChild(radio);
      this.previewTarget.appendChild(container);

      // Render mini preview
      this.renderMiniPreview(miniCanvas, variant);
    });
  }

  renderMiniPreview(canvas, variant) {
    const normalizedDimensions = this.normalizeBoxDimensions(variant);
    const { width: w, height: h, length: l } = normalizedDimensions;

    const scene = new THREE.Scene();
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);

    // Create box
    const geometry = new THREE.BoxGeometry(w, h, l);
    const material = new THREE.MeshStandardMaterial({
      color: 0xeec48e,
      metalness: 0.2,
      roughness: 0.8,
    });

    // Set up camera and scene similar to the main preview
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    const radius = geometry.boundingSphere.radius * 1.5;
    const aspect = canvas.clientWidth / canvas.clientHeight;
    const frustumHeight = radius * 2;
    const frustumWidth = frustumHeight * aspect;

    const camera = new THREE.OrthographicCamera(-frustumWidth / 2, frustumWidth / 2, frustumHeight / 2, -frustumHeight / 2, 0.01, 1000);

    const camDist = radius * 2;
    camera.position.set(camDist, camDist, camDist);
    camera.lookAt(new THREE.Vector3(0, h / 2, 0));

    // Add box to scene
    const box = new THREE.Mesh(geometry, material);
    box.position.y = h / 2;
    scene.add(box);

    // Add light
    const ambient = new THREE.AmbientLight(0xffffff, 0.95);
    const directional = new THREE.DirectionalLight(0xffffff, 0.95);
    directional.position.set(1, 3, 2).normalize();
    scene.add(ambient);
    scene.add(directional);

    // Render
    renderer.render(scene, camera);
  }

  selectVariant(event) {
    const selectedIndex = parseInt(event.target.value);
    this.selectedVariantValue = selectedIndex;

    const containers = this.previewTarget.children;

    Array.from(containers).forEach((container, index) => {
      if (index === selectedIndex) {
        container.classList.add("selected");
      } else {
        container.classList.remove("selected");
      }
    });

    // Update the 3D preview
    this.updateBoxDimensions();
    this.updateLegend();
  }

  confirmVariant() {
    const variant = this.variants[this.selectedVariantValue];
    const shipmentItemElement = document.getElementById(this.elementIdValue);

    if (shipmentItemElement) {
      const lengthInput = shipmentItemElement.querySelector("[data-shipment-item-target='length']");
      const widthInput = shipmentItemElement.querySelector("[data-shipment-item-target='width']");
      const heightInput = shipmentItemElement.querySelector("[data-shipment-item-target='height']");

      if (lengthInput) lengthInput.value = variant.length;
      if (widthInput) widthInput.value = variant.width;
      if (heightInput) heightInput.value = variant.height;
    }
  }

  updateBoxDimensions() {
    const variant = this.variants[this.selectedVariantValue];
    const normalizedDimensions = this.normalizeBoxDimensions(variant);

    // Dispose of old geometry/material to avoid memory leaks and lighting issues
    this.box.geometry.dispose();
    this.box.material.dispose();
    this.scene.remove(this.box);
    this.box = null;

    // Add new box
    const geometry = new THREE.BoxGeometry(normalizedDimensions.width, normalizedDimensions.height, normalizedDimensions.length);
    const material = new THREE.MeshStandardMaterial({
      color: 0xeec48e,
      metalness: 0.2,
      roughness: 0.8,
    });

    this.box = new THREE.Mesh(geometry, material);
    this.box.position.y = normalizedDimensions.height / 2;
    this.scene.add(this.box);

    this.createAxisHelpers();
    this.renderOnce();
  }

  updateLegend() {
    const variant = this.variants[this.selectedVariantValue];
    const { width: w, height: h, length: l } = variant;

    this.legend.innerHTML = "";

    const makeLegendItem = (label, value, color) => {
      const row = document.createElement("div");
      row.innerHTML = `<span style="display:inline-block;width:10px;height:10px;background:${color};margin-right:6px;border-radius:2px;"></span><strong>${label}:</strong> ${value} cm`;
      this.legend.appendChild(row);
    };

    makeLegendItem(this.lengthTextValue, l, "#0000cc");
    makeLegendItem(this.widthTextValue, w, "#ff0000");
    makeLegendItem(this.heightTextValue, h, "#00aa00");
  }

  initScene() {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    const aspect = width / height;

    const variant = this.variants[this.selectedVariantValue];
    const normalizedDimensions = this.normalizeBoxDimensions(variant);
    const w = normalizedDimensions.width;
    const h = normalizedDimensions.height;
    const l = normalizedDimensions.length;

    this.scene = new THREE.Scene();
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setSize(width, height, false);

    const maxDim = Math.max(w, h, l);
    const zoomOutFactor = 1.8; // Reduced from 2.2 for better visibility
    const frustumHeight = Math.max(8, maxDim * zoomOutFactor);
    const frustumWidth = frustumHeight * aspect;

    // Set up camera
    this.camera = new THREE.OrthographicCamera(-frustumWidth / 2, frustumWidth / 2, frustumHeight / 2, -frustumHeight / 2, 0.01, 1000);

    // Position camera
    const camDist = maxDim * 1.5;
    this.camera.position.set(camDist, camDist, camDist);
    this.camera.lookAt(new THREE.Vector3(0, h / 2, 0));

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.95);
    const directional = new THREE.DirectionalLight(0xffffff, 0.95);
    directional.position.set(1, 3, 2).normalize();
    this.scene.add(ambient, directional);

    // Add axis helpers
    this.axesHelper = new THREE.Group();
    this.scene.add(this.axesHelper);
    this.createAxisHelpers();

    // Add patterned walls
    this.createWalls(maxDim);
  }

  createWalls(maxDim) {
    // Create wall size based on max dimension of the box
    const wallSize = maxDim * 4;

    // Create grid textures for the walls
    const floorTexture = this.createGridTexture("#e0e0e0", "#cccccc");
    const backWallTexture = this.createGridTexture("#e6e6fa", "#d8d8f0");
    const sideWallTexture = this.createGridTexture("#f0e6e6", "#e8d8d8");

    // Create walls
    const floor = this.createWall(wallSize, wallSize, floorTexture);
    const backWall = this.createWall(wallSize, wallSize, backWallTexture);
    const sideWall = this.createWall(wallSize, wallSize, sideWallTexture);

    // Position walls
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, 0, 0);

    backWall.position.set(0, wallSize / 2, -wallSize / 2);

    sideWall.rotation.y = Math.PI / 2;
    sideWall.position.set(-wallSize / 2, wallSize / 2, 0);

    // Add walls to scene
    this.scene.add(floor, backWall, sideWall);
  }

  createGridTexture(baseColor, lineColor) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");

    // Fill background
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid lines
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 2;

    const gridSize = 64;

    // Draw vertical lines
    for (let x = 0; x <= canvas.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }

    // Draw horizontal lines
    for (let y = 0; y <= canvas.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 4);

    return texture;
  }

  createWall(width, height, texture) {
    const geometry = new THREE.PlaneGeometry(width, height);
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      side: THREE.DoubleSide,
    });

    return new THREE.Mesh(geometry, material);
  }

  normalizeBoxDimensions(variant) {
    // Target normalized size range: 5x5x6
    const targetMax = 5;
    const targetHeight = 6;

    // Get original dimensions
    const { width: origWidth, height: origHeight, length: origLength } = variant;

    // Handle edge cases with zero or very small dimensions
    if (origWidth <= 0 || origHeight <= 0 || origLength <= 0) {
      return { width: 1, height: 1, length: 1 };
    }

    // Find the scaling factor based on max dimension
    const maxHorizontal = Math.max(origWidth, origLength);
    const scaleFactor = Math.min(targetMax / maxHorizontal, targetHeight / origHeight);

    // Apply scaling
    return {
      width: origWidth * scaleFactor,
      height: origHeight * scaleFactor,
      length: origLength * scaleFactor,
      name: variant.name,
    };
  }

  createAxisHelpers() {
    while (this.axesHelper.children.length > 0) {
      const child = this.axesHelper.children[0];
      this.axesHelper.remove(child);
      child.geometry?.dispose?.();
      child.material?.dispose?.();
    }

    const variant = this.variants[this.selectedVariantValue];
    const normalizedDimensions = this.normalizeBoxDimensions(variant);
    const { width: w, height: h, length: l } = normalizedDimensions;

    const addCylinder = (start, end, color) => {
      const direction = new THREE.Vector3().subVectors(end, start);
      const height = direction.length();
      const orientation = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());
      const maxDim = Math.max(w, h, l);
      const thickness = Math.max(0.02, maxDim * 0.0075);
      const geometry = new THREE.CylinderGeometry(thickness, thickness, height, 8);
      const material = new THREE.MeshBasicMaterial({
        color,
        depthTest: false,
        transparent: false,
      });

      const cylinder = new THREE.Mesh(geometry, material);
      cylinder.position.copy(start.clone().add(direction.multiplyScalar(0.5)));
      cylinder.setRotationFromQuaternion(orientation);
      cylinder.renderOrder = 999;

      this.axesHelper.add(cylinder);
    };

    const halfW = w / 2;
    const halfL = l / 2;

    const pointX1 = new THREE.Vector3(-halfW, 0, halfL);
    const pointX2 = new THREE.Vector3(halfW, 0, halfL);

    const pointY1 = new THREE.Vector3(halfW, 0, halfL);
    const pointY2 = new THREE.Vector3(halfW, h, halfL);

    const pointZ1 = new THREE.Vector3(halfW, 0, halfL);
    const pointZ2 = new THREE.Vector3(halfW, 0, -halfL);

    this.dimensionPoints = {
      x: { start: pointX1, end: pointX2 },
      y: { start: pointY1, end: pointY2 },
      z: { start: pointZ1, end: pointZ2 },
    };

    addCylinder(pointX1, pointX2, 0xff0000); // Red
    addCylinder(pointY1, pointY2, 0x00ff00); // Green
    addCylinder(pointZ1, pointZ2, 0x0000ff); // Blue
  }

  addBox() {
    const variant = this.variants[this.selectedVariantValue];
    const normalizedDimensions = this.normalizeBoxDimensions(variant);
    const { width: w, height: h, length: l } = normalizedDimensions;

    const geometry = new THREE.BoxGeometry(w, h, l);
    const material = new THREE.MeshStandardMaterial({
      color: 0xeec48e,
      metalness: 0,
      roughness: 0.8,
    });

    this.box = new THREE.Mesh(geometry, material);
    this.box.position.y = h / 2; // Sit on ground
    this.scene.add(this.box);
  }

  renderOnce() {
    this.renderer.render(this.scene, this.camera);
  }

  resize() {
    if (this.resizeScheduled) return;

    this.resizeScheduled = true;

    requestAnimationFrame(() => {
      this.resizeScheduled = false;

      if (!this.canvas || !this.renderer || !this.camera) return;

      const width = this.canvas.clientWidth;
      const height = this.canvas.clientHeight;

      // Update renderer size
      this.renderer.setSize(width, height, false);

      // Update camera projection
      const aspect = width / height;

      // Keep the current box in view — recalculate frustum
      const variant = this.variants[this.selectedVariantValue];
      const normalizedDimensions = this.normalizeBoxDimensions(variant);
      const maxDim = Math.max(normalizedDimensions.width, normalizedDimensions.height, normalizedDimensions.length);
      const zoomOutFactor = 1.8;
      const frustumHeight = Math.max(8, maxDim * zoomOutFactor);
      const frustumWidth = frustumHeight * aspect;

      this.camera.left = -frustumWidth / 2;
      this.camera.right = frustumWidth / 2;
      this.camera.top = frustumHeight / 2;
      this.camera.bottom = -frustumHeight / 2;
      this.camera.updateProjectionMatrix();

      // Re-render scene and labels
      this.renderOnce();
    });
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeBound);
  }
}
